import { ActivityIndicator, FlatList, Image } from 'react-native';
import React, { useEffect, useState } from 'react';
import PollOptionCard from './PollOptionCard';
import ResponseTimer from './ResponseTimer';
import type { PollOptionProps, PollProps, PollResponseProps, PollSummaryProps } from '../../types';
import { PollApi } from '../api';
import { Icons } from '../../Components';
import { useColors } from '../../constants/useColors';
import { Button, Text, View } from '../../Components/Themed';

type PollCardProps = {
    poll_id?:string,
    poll_response?:PollResponseProps,
    action_loading?:boolean,
    width:number,
    height:number
    question_number?:number,
    view?:string,
    view_mode:'desktop'|'mobile',
    total_questions?:number,
    onResponse:(response:PollResponseProps) => void,
    onShowAuthenticate: () => void,
    onCancel:() => void,
    onSkip: () => void
}
const sections = ['question','image','options','selected_option', 'correct_select', 'public_results']
const PollCard = ({ poll_id, height, width, view_mode, poll_response, action_loading, onSkip, onCancel, onResponse }: PollCardProps) => {
    const Colors = useColors();
    const [ poll_data, setPollData ] = useState<{
        poll?:PollProps,
        loading:boolean,
        selected_option?:PollOptionProps,
        poll_summaries:PollSummaryProps[],
        poll_options:PollOptionProps[],
    }>({
        loading:false,
        poll_summaries:[],
        poll_options:[]
    });
    const { loading, poll, poll_summaries, poll_options, selected_option } = poll_data;
    const correct_option = poll_options.find(o => o.result_ind == 'win');
    const responses = poll_summaries.reduce((a,b) => a + b.count, 0)
    useEffect(() => {
        if(!poll_id){ return }
        getPollFromServer(poll_id)
    },[poll_id])
    
    const getPollFromServer = async(poll_id:string) => {
        setPollData({ ...poll_data, loading: true });
        const pos_resp = await PollApi.getPollById(poll_id)
        const my_response = pos_resp.poll_options.find(po => po.poll_option_id == poll_response?.poll_option_id);
        setPollData({
            ...poll_data,
            loading:false,
            poll:pos_resp.poll,
            poll_options:pos_resp.poll_options,
            poll_summaries: pos_resp.poll_summaries,
            selected_option: my_response
        })
    }

    const handleCancel = () => {
        setPollData({ ...poll_data, selected_option:undefined })
        onCancel()
    }

    const handleSkip = () => {
        //if(!is_authenticated){ return onShowAuthenticate() }
        //setPollData(DEFAULT_POLL_STATE)
        //setOptionSelected(undefined)
        //if(last_question && onSeeResults){ return onSeeResults() }
        onSkip()

    }

    const handleSubmitResponse = async() => {
        //if(!is_authenticated){ return onShowAuthenticate() }
        if(action_loading || poll_response || !selected_option || !poll){ return }
        let option = poll_options.find(po => po.poll_option_id == selected_option.poll_option_id)
        if(!option){ return }
        //We are good to log the response!
        let draft_response:PollResponseProps = {
            poll_id:poll.poll_id,
            poll_option_id:option.poll_option_id,
            stake: poll.minimum_stake,
            market_type: 'FREE',
            poll_response_id: '',
            player_id: '',
            winnings: 0,
            response_body: {},
            status: 'pending',
            create_datetime: '',
            last_update_datetime: ''
        }
        if(option.option_type == 'input'){
            draft_response.input_value = selected_option.option_value
        }
        onResponse(draft_response);
        //setPollData({ ...poll_data, selected_option:undefined })
    }

    const handleTimesUp = async() => {
        if(selected_option){
            return handleSubmitResponse()
        }
        if(!poll || poll.status != 'active'){ return }
        if(poll_response){ return }
        const draft_response:PollResponseProps = {
            poll_id:poll.poll_id,
            poll_option_id:'0',
            stake: poll.minimum_stake,
            market_type: 'FREE',
            poll_response_id: '',
            player_id: '',
            winnings: 0,
            timed_out: true,
            response_body: {},
            status: 'pending',
            create_datetime: '',
            last_update_datetime: ''
        }
        onResponse(draft_response)
    }

    const renderPollOptions = (data: { item:PollOptionProps, index:number }) => {
        if(!poll){ return <></ >}
        const poll_summary = poll_summaries.find(ps => ps.poll_option_id == data.item.poll_option_id);
        const draft_selected = selected_option?.poll_option_id == data.item.poll_option_id ? true : false;
        return (
            <View style={{ margin:5, flex:1 }}>
                <PollOptionCard
                    poll_summary={poll_summary}
                    draft_selected={draft_selected}
                    poll_response={poll_response}
                    poll_option={data.item}
                    show_summary={(poll_response) && poll.show_responses ? true : false}
                    onSelect={(poll_option) => {
                        if(poll_response){ return }
                        if(poll?.status !== 'active'){ return }
                        if(draft_selected){ 
                            setPollData({ ...poll_data, selected_option: undefined }) 
                        } else { 
                            setPollData({ ...poll_data, selected_option: poll_option })
                        }
                    }}
                />
            </View>
        )
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'question':
                if(!poll){ return <></> }
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:15, borderTopRightRadius:22, borderTopLeftRadius:22 }}>
                        <View transparent style={{ flex:1 }}>
                            <Text theme='h2'>Question {poll.priority}</Text>
                            <Text style={{ marginTop:5 }} theme='h1'>{poll.poll_question}</Text>
                        </View>
                    </View>
                )
            case 'image':
                if(!poll?.poll_image?.url){ return <></> }
                return (
                    <View nativeID='question_image'>
                        <Image
                            source={{ uri:poll.poll_image.url }}
                            style={{ width:width - 20, height:width/2}}
                            resizeMode='cover'
                        />
                    </View>
                )
            case 'options':
                if(!poll || poll.status != 'active' || poll_response){ return <></> }
                return (
                    <View nativeID='question_respond_options' style={{ padding:12, maxHeight:550 }}>
                        <FlatList
                            data={poll_options.sort((a,b) => a.priority - b.priority)}
                            renderItem={renderPollOptions}
                            numColumns={2}
                            keyExtractor={(item) => item.poll_option_id.toString()}
                        />
                    </View>
                )
            case 'selected_option':
                if(!selected_option || !poll_response){ return <></> }
                return (
                    <View nativeID='selected_option' style={{ marginBottom:10, padding:10 }}>
                        <Text style={{ marginBottom:5 }} size={14} theme='description'>MY SELECTED ANSWER</Text>
                        <PollOptionCard
                            show_result={true}
                            show_summary={false}
                            poll_response={poll_response}
                            onSelect={() => console.log('Hey')}
                            poll_option={selected_option}
                        />
                    </View>
                )
            case 'correct_select':
                if(!poll || poll.poll_type != 'select' || !correct_option){ return <></> }
                if(poll.status != 'closed' && correct_option && !poll_response){ return <></> }
                return (
                    <View nativeID='selected_option' style={{ marginBottom:10, padding:10 }}>
                        <Text style={{ marginBottom:5 }} size={14} theme='description' weight='semibold'>CORRECT ANSWER</Text>
                        <PollOptionCard
                            show_summary={true}
                            show_result={true}
                            onSelect={() => console.log('')}
                            poll_option={correct_option}
                        />
                    </View>
                )
            case 'correct_input':
                if(!poll || poll.poll_type != 'input' || !poll.winning_value){ return <></> }
                return (
                    <View style={{ marginBottom:10, padding:10 }}>
                        <Text style={{ marginBottom:5 }} size={14} theme='description' weight='semibold'>CORRECT ANSWER</Text>
                        <View style={{ flexDirection:'row', alignItems:'center', padding:10, borderRadius:22, borderWidth:1, borderColor:Colors.text.success }}>
                            <Text style={{ flex:1 }} size={14} color={Colors.text.success} textAlign='center' weight='semibold'>{poll.winning_value}</Text>
                            <Icons.CheckIcon color={Colors.text.success} size={12}/>
                        </View>
                    </View>
                )
            case 'public_results':
                if(!poll || poll.poll_type != 'select' || !poll_response){ return <></> }
                if(!poll.show_responses){ return <></> }
                return (
                    <View nativeID='option_results' style={{ padding:10, borderRadius:8 }}>
                        <View style={{ flexDirection:'row', alignItems:'center', marginBottom:5 }}>
                            <Text style={{ flex:1 }} size={14} theme='description' weight='semibold'>PUBLIC RESULTS</Text>
                            <View style={{ borderRadius:22, padding:10 }}>
                                <Text size={14} theme='description'>{responses} Responses</Text>
                            </View>
                        </View>
                        {!poll.show_responses ?
                        <Text style={{ marginBottom:5 }} color={Colors.text.warning} size={12} >Responses are hidden until closed</Text>
                        :<></>}
                        <FlatList
                            data={poll_options}
                            renderItem={renderPollOptions}
                            keyExtractor={(item) => item.poll_option_id.toString()}
                        />
                    </View>
                )
            default:return <></>
        }
    }
    
    return (
        <View float nativeID='poll' style={{  borderRadius:22, maxHeight: height-40, width: view_mode == 'desktop'?500:undefined }}>
            <View transparent style={{ flex:1 }}>
                {loading || !poll ?
                <ActivityIndicator style={{ padding:20, alignSelf:'center' }} color={Colors.text.h1} size='large' />
                :<></>}
                <FlatList
                    data={sections}
                    key={'poll_question_sections'}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
            </View>      
            <View nativeID='question_actions' type='footer' style={{ flexDirection:'row', padding:12, borderBottomLeftRadius:22, borderBottomRightRadius:22 }}>
                {poll && (!poll.seconds_allowed || poll_response) ?
                <Button
                    style={{ marginRight:10, justifyContent:'center', padding:15, backgroundColor:Colors.text.warning, borderRadius:22, borderWidth:1, borderColor:Colors.text.warning }}
                    onPress={() => handleCancel()}
                >
                    <Icons.ListIcon size={16} color={Colors.text.white} />
                </Button>
                :<></>}
                {poll &&poll.status == 'active' && poll.seconds_allowed && !poll_response ?
                    <View style={{ marginRight:5 }}>
                        <ResponseTimer
                            poll_id={poll.poll_id}
                            seconds={poll.seconds_allowed}
                            onTimesUp={() => handleTimesUp()}
                        />
                    </View>
                :
                <Button
                    style={{flex:1, marginRight:5, padding:15}}
                    type='action'
                    title={poll_response?'Next':'Skip'}
                    onPress={() => handleSkip()}
                />
                }
                {poll && !poll_response && !loading ?
                <Button
                    style={{flex:2, padding:15, opacity: selected_option && !action_loading ? 1 : 0.5}}
                    loading={action_loading}
                    disabled={selected_option&&!action_loading?false:true}
                    type='success'
                    title={selected_option?`Submit!`:poll.status == 'active' ? 'Select Answer' : 'Not Active'}
                    onPress={() => handleSubmitResponse()}
                />
                :<></>}
            </View>
        </View>
        
    )
}

export default PollCard